import { Product } from '@interfaces/models/product';
import { AlertCriterion } from '@interfaces/models/alertCriterion';
import { AlertSettings } from '@interfaces/models/alertSettings';
import AlertsService from '@services/alerts-service';
import ProductService from '@services/product-service';
import { useMutation, useQuery } from '@tanstack/react-query';
import SellCatalogService from '@services/sell-service';
import { PriceDropRecommendedPriceQueryKeys } from '@enums/react-query-keys';
import logger from '@helpers/utils/logger/client';

type PriceZoneQueryProps = {
  recommendedPrice: number;
  sellerPrice: number;
  enabled: boolean;
};

type DropPriceOptions = {
  onSuccess: () => void;
  onError: (error) => void;
};

const useProductPriceDrop = (userId: User['id'], productId: Product['id']) => {
  const createPriceDropAlert = async (): Promise<void> => {
    const criteria: AlertCriterion[] = [
      {
        key: 'idProduct',
        value: [productId],
      },
    ];
    const data: AlertSettings = {
      category: 'priceDrop',
      criteria,
    };
    await AlertsService.createAlert(data);
  };

  const deletePriceDropAlert = async (): Promise<void> => {
    await ProductService.deletePriceDrop(productId);
  };

  const useGetPriceDetails = (newPrice: number, enabled = false) => {
    return useQuery({
      keepPreviousData: true,
      useErrorBoundary: false,
      queryFn: () => {
        return SellCatalogService.getPriceDetails(newPrice, productId);
      },
      queryKey: [PriceDropRecommendedPriceQueryKeys.PRICE_DETAILS, productId, newPrice],
      onError: (error: Error) => {
        logger.error(`Error fetching recommended price on price-drop page : ${error}`);
      },
      select: (data) => data.data,
      enabled,
    });
  };

  const usePriceRecommendation = () => {
    return useQuery({
      queryFn: () => SellCatalogService.fetchPriceDropRecommendation(productId),
      queryKey: [PriceDropRecommendedPriceQueryKeys.PRICE_DROP_RECOMMENDED, productId],
      onError: (error) => {
        logger.error(`Error fetching recommended price on price-drop page : ${error}`);
      },
      select: (data) => data,
    });
  };

  const usePriceZone = ({ recommendedPrice, sellerPrice, enabled }: PriceZoneQueryProps) => {
    return useQuery({
      keepPreviousData: true,
      queryFn: () =>
        SellCatalogService.fetchPriceZone({
          userId,
          productId,
          recommendedPrice,
          sellerPrice,
        }),
      queryKey: [PriceDropRecommendedPriceQueryKeys.PRICE_DROP_ZONE, productId, recommendedPrice, sellerPrice, userId],
      enabled,
      onError: (error) => {
        logger.error(`Error fetching price zone on price-drop page : ${error}`);
      },
      select: (data) => data.priceZone,
    });
  };

  const useDropPrice = ({ onSuccess, onError }: DropPriceOptions) => {
    return useMutation({
      mutationKey: [PriceDropRecommendedPriceQueryKeys.DROP_PRICE, userId, productId],
      mutationFn: (newPrice: number) => SellCatalogService.dropPrice(newPrice, productId, userId),
      onSuccess,
      onError,
    });
  };

  return {
    createPriceDropAlert,
    usePriceZone,
    deletePriceDropAlert,
    useDropPrice,
    usePriceRecommendation,
    useGetPriceDetails,
  };
};

export default useProductPriceDrop;
